import type {
  ApiErrorBody, Billing, Blueprint, Build, Catalog, Deployment, Fleet,
  GeneratedFile, GeneratedFileMeta, LedgerEntry, ListResponse, Project, Tenant, User,
} from "./types";

// The typed API client. One place that knows about the wire.

const BASE = "/api";
const TOKEN_KEY = "atelier.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t: string | null) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

/** Carries the server's code so callers can branch on the reason rather than
 *  string-matching a message. `insufficient_credits` and `quota_exceeded` both
 *  arrive as 402 and the console routes them differently. */
export class HttpError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(BASE + path, { ...init, headers });

  // A 401 on an ordinary request means the session is gone — expired, revoked,
  // or the account was deleted — and clearing the stale token is what stops
  // the console looping on a credential that will never work again.
  //
  // The sign-in endpoints are the exception, and it matters: there a 401 means
  // "that code is wrong", and blanket-mapping it would tell someone who
  // fat-fingered a digit that their session had expired — while they were in
  // the middle of creating one.
  const isSignIn = path.startsWith("/auth/request-code") || path.startsWith("/auth/verify");
  if (res.status === 401 && !isSignIn) {
    setToken(null);
    throw new HttpError(401, "unauthenticated", "Your session has expired. Sign in again.");
  }
  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      // A non-JSON error body means something in front of the app answered — a
      // proxy, a 502 page. Say that rather than showing a parse error.
    }
    throw new HttpError(
      res.status,
      body?.error?.code ?? "http_error",
      body?.error?.message ?? `Request failed (${res.status})`,
      body?.error?.fields
    );
  }
  return (await res.json()) as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") s.set(k, String(v));
  }
  const out = s.toString();
  return out ? `?${out}` : "";
}

export const api = {
  catalog: () => request<Catalog>("/catalog"),

  auth: {
    requestCode: (email: string) =>
      request<{ sent: boolean }>("/auth/request-code", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    verify: (email: string, code: string, org?: string) =>
      request<{ token: string; user: User; new_account: boolean }>("/auth/verify", {
        method: "POST",
        body: JSON.stringify({ email, code, org }),
      }),
    me: () => request<{ user: User; tenant: Tenant }>("/auth/me"),
    signOut: () => request<void>("/auth/signout", { method: "POST" }),
  },

  tenant: {
    get: () => request<{ tenant: Tenant; projects: number; deployed_apps: number }>("/tenant"),
    update: (name: string) =>
      request<Tenant>("/tenant", { method: "PATCH", body: JSON.stringify({ name }) }),
    members: () => request<ListResponse<User>>("/tenant/members"),
    invite: (email: string, role: string) =>
      request<User>("/tenant/members", { method: "POST", body: JSON.stringify({ email, role }) }),
    removeMember: (id: string) =>
      request<void>(`/tenant/members/${encodeURIComponent(id)}`, { method: "DELETE" }),
  },

  projects: {
    list: (p: { q?: string; limit?: number; offset?: number } = {}) =>
      request<ListResponse<Project>>(`/projects${qs(p)}`),
    create: (body: {
      prompt: string;
      name?: string;
      surfaces: string[];
      features: string[];
      plan?: boolean;
    }) =>
      request<{ project: Project; blueprint?: Blueprint; plan_error?: string }>("/projects", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    get: (id: string) =>
      request<{
        project: Project;
        builds: Build[];
        deployments: Deployment[];
        blueprint?: Blueprint;
        database?: { engine: string; host: string; name: string; user: string; status: string };
      }>(`/projects/${encodeURIComponent(id)}`),
    update: (id: string, body: Partial<{ name: string; prompt: string; surfaces: string[]; features: string[] }>) =>
      request<Project>(`/projects/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    archive: (id: string) =>
      request<void>(`/projects/${encodeURIComponent(id)}`, { method: "DELETE" }),

    plan: (id: string) =>
      request<Blueprint>(`/projects/${encodeURIComponent(id)}/plan`, { method: "POST" }),
    blueprint: (id: string) => request<Blueprint>(`/projects/${encodeURIComponent(id)}/blueprint`),
    putBlueprint: (id: string, bp: Blueprint) =>
      request<Blueprint>(`/projects/${encodeURIComponent(id)}/blueprint`, {
        method: "PUT",
        body: JSON.stringify(bp),
      }),

    builds: (id: string) => request<ListResponse<Build>>(`/projects/${encodeURIComponent(id)}/builds`),
    startBuild: (id: string, fill?: boolean) =>
      request<Build>(`/projects/${encodeURIComponent(id)}/builds`, {
        method: "POST",
        body: JSON.stringify({ fill }),
      }),

    deploy: (id: string) =>
      request<Deployment>(`/projects/${encodeURIComponent(id)}/deploy`, { method: "POST" }),
    deployments: (id: string) =>
      request<ListResponse<Deployment>>(`/projects/${encodeURIComponent(id)}/deployments`),

    secrets: (id: string) =>
      request<ListResponse<{ key: string; hint: string; updated_at: string }>>(
        `/projects/${encodeURIComponent(id)}/secrets`
      ),
    putSecret: (id: string, key: string, value: string) =>
      request<{ key: string; hint: string }>(`/projects/${encodeURIComponent(id)}/secrets`, {
        method: "PUT",
        body: JSON.stringify({ key, value }),
      }),
    deleteSecret: (id: string, key: string) =>
      request<void>(`/projects/${encodeURIComponent(id)}/secrets/${encodeURIComponent(key)}`, {
        method: "DELETE",
      }),
  },

  builds: {
    get: (id: string) => request<Build>(`/builds/${encodeURIComponent(id)}`),
    files: (id: string) => request<ListResponse<GeneratedFileMeta>>(`/builds/${encodeURIComponent(id)}/files`),
    file: (id: string, path: string) =>
      request<GeneratedFile>(`/builds/${encodeURIComponent(id)}/files/${path}`),
    cancel: (id: string) =>
      request<void>(`/builds/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
    downloadURL: (id: string) => `${BASE}/builds/${encodeURIComponent(id)}/download`,
    /** The log is plain text, not JSON — it goes straight into a <pre>. */
    log: async (id: string): Promise<string> => {
      const token = getToken();
      const res = await fetch(`${BASE}/builds/${encodeURIComponent(id)}/log`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return "";
      return res.text();
    },
  },

  deployments: {
    rollback: (id: string) =>
      request<Deployment>(`/deployments/${encodeURIComponent(id)}/rollback`, { method: "POST" }),
  },

  billing: {
    get: () => request<Billing>("/billing"),
    ledger: (p: { limit?: number; offset?: number } = {}) =>
      request<ListResponse<LedgerEntry>>(`/billing/ledger${qs(p)}`),
    checkout: (credits: number) =>
      request<{ simulated: boolean; credits: number; message?: string; note?: string }>(
        "/billing/checkout",
        { method: "POST", body: JSON.stringify({ credits }) }
      ),
  },

  admin: {
    tenants: (p: { q?: string; limit?: number } = {}) =>
      request<ListResponse<Tenant & { users: number; projects: number; live_apps: number; builds_30d: number }>>(
        `/admin/tenants${qs(p)}`
      ),
    builds: (p: { status?: string; limit?: number } = {}) =>
      request<ListResponse<Build>>(`/admin/builds${qs(p)}`),
    fleet: () => request<Fleet>("/admin/fleet"),
    grantCredits: (tenantId: string, credits: number, reason: string) =>
      request<Tenant>(`/admin/tenants/${encodeURIComponent(tenantId)}/credits`, {
        method: "POST",
        body: JSON.stringify({ credits, reason }),
      }),
    suspend: (tenantId: string, suspended: boolean, reason: string) =>
      request<Tenant>(`/admin/tenants/${encodeURIComponent(tenantId)}/suspend`, {
        method: "POST",
        body: JSON.stringify({ suspended, reason }),
      }),
  },
};

/** The download link needs the bearer token, and an <a href> cannot carry a
 *  header. Fetching the blob and handing it to the browser is the way to keep
 *  the endpoint authenticated rather than adding a token query parameter,
 *  which would end up in server logs and browser history. */
export async function downloadBuild(buildId: string, filename: string) {
  const token = getToken();
  const res = await fetch(api.builds.downloadURL(buildId), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new HttpError(res.status, "download_failed", "Could not download that build.");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
